import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Plus, Tv2, ChevronRight } from 'lucide-react';

interface Series {
  id: string;
  title: string;
  description: string | null;
  status: string;
  created_at: string;
  season_count: number;
}

export default function SeriesList() {
  const { publisher } = useAuth();
  const navigate = useNavigate();
  const [series, setSeries] = useState<Series[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (publisher?.id) fetchSeries();
  }, [publisher?.id]);

  const fetchSeries = async () => {
    if (!publisher?.id) return;
    setLoading(true);
    try {
      const { data: works, error } = await supabase
        .from('works')
        .select('id, title, description, status, created_at')
        .eq('publisher_id', publisher.id)
        .eq('type', 'tv_show')
        .neq('status', 'archived')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch season counts for all shows
      type WorkRow = Omit<Series, 'season_count'>;
      const rows = (works || []) as WorkRow[];
      const workIds = rows.map(w => w.id);
      let seasonCounts: Record<string, number> = {};
      if (workIds.length > 0) {
        const { data: seasons } = await supabase
          .from('seasons')
          .select('work_id')
          .in('work_id', workIds);
        if (seasons) {
          for (const s of seasons) {
            seasonCounts[s.work_id] = (seasonCounts[s.work_id] ?? 0) + 1;
          }
        }
      }

      setSeries(
        rows.map(w => ({ ...w, season_count: seasonCounts[w.id] ?? 0 }))
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">TV Series</h1>
          <p className="text-muted-foreground mt-1">Manage your TV shows and their episodes</p>
        </div>
        <Button onClick={() => navigate('/content/series/new')}>
          <Plus className="h-4 w-4 mr-2" />
          New Series
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : series.length === 0 ? (
        <Card className="partner-card">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Tv2 className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">No series yet</h3>
              <p className="text-muted-foreground text-sm mt-1">
                Create your first TV series to start adding seasons and episodes.
              </p>
            </div>
            <Button onClick={() => navigate('/content/series/new')}>
              <Plus className="h-4 w-4 mr-2" />
              Create Series
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {series.map(show => (
            <Card key={show.id} className="partner-card kinora-editorial-card">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="kinora-eyebrow">series</p>
                    <CardTitle className="font-display text-lg truncate mt-1">{show.title}</CardTitle>
                    {show.description && (
                      <CardDescription className="mt-1 line-clamp-2 font-display italic">
                        {show.description}
                      </CardDescription>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className="capitalize">{show.status}</Badge>
                    <Badge variant="secondary">{show.season_count} season{show.season_count !== 1 ? 's' : ''}</Badge>
                    <Button variant="ghost" size="sm" asChild>
                      <Link to={`/content/series/${show.id}`}>
                        Manage
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
